import {
    DataGridCellHighlight,
    DataGridColumn,
    DataGridColumnHighlightingMechanisms,
    DataGridColumnTypes,
    DataGridExoticColumnFields,
    DataGridRow,
} from "../../../models";

export interface DataGridHighlightSegment {
    text: string,
    highlight: boolean,
    cluster: number,
    degree: number,
}

/** Values may differ in up to about a third of their characters and still share a family. */
const MAX_FAMILY_DIFF_RATIO = 0.34;
const HIGHLIGHT_CLUSTER_COUNT = 6;

export interface DataGridHighlightTextInvokeArg {
    format?: (value: any) => any,
    detailedPanelOpen?: boolean,
    toggleDetailedPanelOpen?: () => void,
    groupedRowsOpen?: boolean,
    toggleGroupedRowsOpen?: () => void,
    rowSelected?: boolean,
    toggleRowSelection?: () => void,
    openEditPopover?: () => void,
}

const REACT_ELEMENT_TYPE = Symbol.for('react.element');
const REACT_TRANSITIONAL_ELEMENT_TYPE = Symbol.for('react.transitional.element');

const noop = () => undefined;

/**
 * Builds the lookup key for a row/column highlight plan.
 * @param rowKey
 * @param columnName
 */
export function highlightMapKey(rowKey: unknown, columnName: string): string {
    return `${String(rowKey)}\u0000${columnName}`;
}

/**
 * True when the value is a React element. Avoids importing React into this helper.
 * @param value
 */
function isReactElement(value: unknown): boolean {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const type = (value as { $$typeof?: unknown }).$$typeof;
    return type === REACT_ELEMENT_TYPE || type === REACT_TRANSITIONAL_ELEMENT_TYPE;
}

/**
 * Turns a primitive, or an object with a useful toString, into comparison text.
 * Empty strings and plain objects are skipped.
 * @param value
 */
function toHighlightText(value: unknown): string | null {
    if (value == null || typeof value === 'function' || isReactElement(value)) {
        return null;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        const text = String(value);
        return text === '' ? null : text;
    }
    const text = String(value);
    if (text === '' || text === '[object Object]') {
        return null;
    }
    return text;
}

/**
 * Reads the cell value out of a raw row cell, which may be a primitive, an element, a render function, or a cell object.
 * @param rawCell
 */
function unwrapCellContent(rawCell: unknown): unknown {
    if (rawCell == null || typeof rawCell === 'function' || typeof rawCell !== 'object') {
        return rawCell;
    }
    if (isReactElement(rawCell)) {
        return rawCell;
    }
    if (Object.prototype.hasOwnProperty.call(rawCell, 'value')) {
        return (rawCell as { value: unknown }).value;
    }
    return rawCell;
}

/**
 * Resolves comparison text for a non-function cell value, preferring the formatted string the grid renders.
 * @param column
 * @param value
 */
function resolveValueHighlightText(
    column: Pick<DataGridColumn, 'type' | 'format'>,
    value: unknown,
): string | null {
    if (value == null || typeof value === 'function' || isReactElement(value)) {
        return null;
    }

    if (typeof column.format === 'function') {
        try {
            const formatted = column.format(value);
            if (formatted == null) {
                return null;
            }
            const formattedText = toHighlightText(formatted);
            if (formattedText != null) {
                return formattedText;
            }
        } catch {
            // Fall through to the raw value when a formatter fails.
        }
    } else if (typeof value === 'number' && column.type === DataGridColumnTypes.number && Number.isFinite(value)) {
        return value.toFixed(2);
    }

    return toHighlightText(value);
}

/**
 * Resolves the string used to compare one cell with the rest of its column.
 *
 * Render functions may pass that string through `getHighlightText`. Otherwise a primitive return value,
 * the column formatter (money, date, date-time, or a custom format), or `String(value)` is used.
 *
 * @param column
 * @param rawCell
 * @param invokeArg
 */
export function resolveCellHighlightText(
    column: Pick<DataGridColumn, 'type' | 'format'>,
    rawCell: unknown,
    invokeArg: DataGridHighlightTextInvokeArg = {},
): string | null {
    const value = unwrapCellContent(rawCell);
    if (typeof value !== 'function') {
        return resolveValueHighlightText(column, value);
    }

    let captured: unknown;
    let capturedSet = false;
    let returned: unknown;
    try {
        returned = value({
            format: invokeArg.format ?? column.format,
            detailedPanelOpen: invokeArg.detailedPanelOpen ?? false,
            toggleDetailedPanelOpen: invokeArg.toggleDetailedPanelOpen ?? noop,
            groupedRowsOpen: invokeArg.groupedRowsOpen ?? false,
            toggleGroupedRowsOpen: invokeArg.toggleGroupedRowsOpen ?? noop,
            rowSelected: invokeArg.rowSelected ?? false,
            toggleRowSelection: invokeArg.toggleRowSelection ?? noop,
            openEditPopover: invokeArg.openEditPopover ?? noop,
            getHighlightText: (text: unknown) => {
                captured = text;
                capturedSet = true;
                return text == null ? '' : String(text);
            },
        });
    } catch {
        if (capturedSet) {
            return toHighlightText(captured);
        }
        return null;
    }

    if (capturedSet) {
        return toHighlightText(captured);
    }
    return toHighlightText(returned);
}

/**
 * Longest common subsequence index pairs, preferring earlier matches on ties.
 * @param a
 * @param b
 */
function lcsIndexPairs(a: string, b: string): Array<{ ia: number, ib: number }> {
    const n = a.length;
    const m = b.length;
    if (!n || !m) {
        return [];
    }

    const dp: number[][] = Array.from({length: n + 1}, () => Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            if (a[i - 1] === b[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }

    const pairs: Array<{ ia: number, ib: number }> = [];
    let i = n;
    let j = m;
    while (i > 0 && j > 0) {
        if (a[i - 1] === b[j - 1] && dp[i][j] === dp[i - 1][j - 1] + 1) {
            pairs.push({ia: i - 1, ib: j - 1});
            i--;
            j--;
        } else if (dp[i - 1][j] >= dp[i][j - 1]) {
            i--;
        } else {
            j--;
        }
    }
    pairs.reverse();
    return pairs;
}

/**
 * Marks characters that belong to a subsequence shared by every string.
 * @param strings
 */
export function buildCommonMasks(strings: string[]): boolean[][] {
    if (strings.length < 2) {
        return strings.map(text => Array(text.length).fill(false));
    }

    let commonStr = strings[0];
    let indexMaps: number[][] = [strings[0].split('').map((_, index) => index)];

    for (let index = 1; index < strings.length; index++) {
        const matches = lcsIndexPairs(commonStr, strings[index]);
        const nextCommon: string[] = [];
        const nextMaps: number[][] = indexMaps.map(() => []);
        const currentMap: number[] = [];
        for (const match of matches) {
            nextCommon.push(commonStr[match.ia]);
            for (let mapIndex = 0; mapIndex < indexMaps.length; mapIndex++) {
                nextMaps[mapIndex].push(indexMaps[mapIndex][match.ia]);
            }
            currentMap.push(match.ib);
        }
        nextMaps.push(currentMap);
        indexMaps = nextMaps;
        commonStr = nextCommon.join('');
    }

    return strings.map((text, index) => {
        const mask = Array(text.length).fill(false);
        for (const charIndex of indexMaps[index] ?? []) {
            if (charIndex >= 0 && charIndex < mask.length) {
                mask[charIndex] = true;
            }
        }
        return mask;
    });
}

/**
 * Marks characters of `text` that belong to `common`, which is itself a subsequence.
 * @param text
 * @param common
 */
export function lcsMask(text: string, common: string): boolean[] {
    const mask = Array(text.length).fill(false);
    if (!text || !common) {
        return mask;
    }
    for (const pair of lcsIndexPairs(text, common)) {
        mask[pair.ia] = true;
    }
    return mask;
}

/**
 * Share of characters that differ. Equal-length codes use a direct position compare.
 * Other pairs use the longest common subsequence.
 * @param left
 * @param right
 */
function differenceRatio(left: string, right: string): number {
    if (left === right) {
        return 0;
    }
    const maxLength = Math.max(left.length, right.length);
    if (!maxLength) {
        return 0;
    }
    if (left.length === right.length) {
        let differences = 0;
        for (let index = 0; index < left.length; index++) {
            if (left[index] !== right[index]) {
                differences++;
            }
        }
        return differences / left.length;
    }
    return 1 - (lcsIndexPairs(left, right).length / maxLength);
}

/**
 * Groups strings that share a base.
 * Closest families merge first, and only when every pair across the merge still shares that base.
 * @param texts
 */
function clusterBySimilarity(texts: string[]): number[] {
    const count = texts.length;
    const ratios = Array.from({length: count}, () => Array(count).fill(0));
    for (let left = 0; left < count; left++) {
        for (let right = left + 1; right < count; right++) {
            const ratio = differenceRatio(texts[left], texts[right]);
            ratios[left][right] = ratio;
            ratios[right][left] = ratio;
        }
    }

    let families: number[][] = texts.map((_, index) => [index]);
    while (families.length > 1) {
        let best = Infinity;
        let leftFamily = 0;
        let rightFamily = 1;
        for (let left = 0; left < families.length; left++) {
            for (let right = left + 1; right < families.length; right++) {
                let distance = 0;
                for (const leftIndex of families[left]) {
                    for (const rightIndex of families[right]) {
                        if (ratios[leftIndex][rightIndex] > distance) {
                            distance = ratios[leftIndex][rightIndex];
                        }
                    }
                }
                if (distance < best) {
                    best = distance;
                    leftFamily = left;
                    rightFamily = right;
                }
            }
        }
        if (best > MAX_FAMILY_DIFF_RATIO) {
            break;
        }
        const merged = families[leftFamily].concat(families[rightFamily]);
        families = families.filter((_, index) => index !== leftFamily && index !== rightFamily);
        families.push(merged);
    }

    const membership = Array(count).fill(0);
    families.forEach((members, id) => {
        members.forEach(index => {
            membership[index] = id;
        });
    });
    return membership;
}

/**
 * Maps how rare a character is inside its family to a 1–3 degree. 0 means it is the shared base.
 * @param count
 * @param size
 * @param modeCount
 */
function degreeForCount(count: number, size: number, modeCount: number): number {
    if (count === size) {
        return 0;
    }
    if (modeCount > size / 2 && count === modeCount) {
        return 0;
    }
    if (count === 1 && size >= 3) {
        return 3;
    }
    const rarity = 1 - count / size;
    if (rarity >= 0.8) {
        return 3;
    }
    if (rarity >= 0.5) {
        return 2;
    }
    return 1;
}

/**
 * Scores each character against the other values in the same family.
 * Same-length codes are compared by position. A slot that is not identical is marked on every row:
 * the majority digit is degree 1, and rarer digits keep a higher degree.
 * Mixed lengths use pairwise common subsequences.
 * @param texts
 */
function familyDegrees(texts: string[]): number[][] {
    if (texts.length < 2) {
        return texts.map(text => Array(text.length).fill(0));
    }
    const sameLength = texts.every(text => text.length === texts[0].length);
    if (!sameLength) {
        const matchCounts = texts.map(text => Array(text.length).fill(0));
        for (let left = 0; left < texts.length; left++) {
            for (let right = left + 1; right < texts.length; right++) {
                for (const pair of lcsIndexPairs(texts[left], texts[right])) {
                    matchCounts[left][pair.ia]++;
                    matchCounts[right][pair.ib]++;
                }
            }
        }
        const others = texts.length - 1;
        return matchCounts.map(counts => counts.map(count => {
            const modeCount = count > others / 2 ? count : 0;
            return degreeForCount(count, others, modeCount);
        }));
    }

    const length = texts[0].length;
    const columns = Array.from({length}, () => new Map<string, number>());
    for (const text of texts) {
        for (let index = 0; index < length; index++) {
            const character = text[index];
            columns[index].set(character, (columns[index].get(character) ?? 0) + 1);
        }
    }
    const degrees = texts.map(text => text.split('').map((character, index) => {
        const count = columns[index].get(character) ?? 0;
        let modeCount = 0;
        columns[index].forEach(value => {
            if (value > modeCount) {
                modeCount = value;
            }
        });
        return degreeForCount(count, texts.length, modeCount);
    }));
    for (let index = 0; index < length; index++) {
        const slotVaries = degrees.some(row => row[index] > 0);
        if (!slotVaries) {
            continue;
        }
        for (const row of degrees) {
            if (row[index] === 0) {
                row[index] = 1;
            }
        }
    }
    return degrees;
}

/**
 * Builds the highlight mask for a mode from per-character degrees.
 * Diff marks disagreements. Eq marks the shared base, and only when the family is not identical.
 * @param degrees
 * @param mode
 */
function maskFromDegrees(degrees: number[], mode: DataGridColumnHighlightingMechanisms): boolean[] {
    const varies = degrees.some(degree => degree > 0);
    const hasShared = degrees.some(degree => degree === 0);
    return degrees.map(degree => (
        mode === DataGridColumnHighlightingMechanisms.eq
            ? varies && hasShared && degree === 0
            : degree > 0
    ));
}

/**
 * Underline weight for each character. Similarity mode uses one light weight so a long shared base stays readable.
 * @param degrees
 * @param mode
 */
function visualDegrees(degrees: number[], mode: DataGridColumnHighlightingMechanisms): number[] {
    if (mode === DataGridColumnHighlightingMechanisms.eq) {
        return degrees.map(() => 1);
    }
    return degrees;
}

export interface DataGridHighlightStyle {
    mask: boolean[],
    degrees: number[],
    cluster: number,
}

/**
 * Picks the mask, degree, and family color for the string about to be rendered.
 * @param text
 * @param highlight
 */
export function comparisonStyleForText(
    text: string,
    highlight: DataGridCellHighlight | null | undefined,
): DataGridHighlightStyle | null {
    if (!highlight?.active || highlight.cluster == null || highlight.cluster < 0) {
        return null;
    }
    const mode = highlight.mode;
    if (highlight.text != null && text === highlight.text && highlight.degrees && highlight.degrees.length === text.length) {
        return {
            mask: maskFromDegrees(highlight.degrees, mode),
            degrees: visualDegrees(highlight.degrees, mode),
            cluster: highlight.cluster,
        };
    }
    if (!highlight.common) {
        return null;
    }
    const shared = lcsMask(text, highlight.common);
    const degrees = shared.map(isShared => isShared ? 0 : 2);
    const mask = maskFromDegrees(degrees, mode);
    if (!mask.some(Boolean)) {
        return null;
    }
    return {
        mask,
        degrees: visualDegrees(degrees, mode),
        cluster: highlight.cluster,
    };
}

/**
 * Groups a string into runs. Adjacent characters merge when they share a highlight, family, and degree.
 * @param text
 * @param mask
 * @param degrees
 * @param cluster
 */
export function buildHighlightSegments(
    text: string,
    mask: boolean[] | null,
    degrees: number[] | null = null,
    cluster: number | null = null,
): DataGridHighlightSegment[] {
    if (!text) {
        return [];
    }
    if (!mask || mask.length !== text.length) {
        return [{text, highlight: false, cluster: -1, degree: 0}];
    }

    const segments: DataGridHighlightSegment[] = [];
    for (let index = 0; index < text.length; index++) {
        const highlight = !!mask[index];
        const degree = highlight ? Math.max(1, degrees?.[index] ?? 1) : 0;
        const segmentCluster = highlight ? (cluster ?? 0) : -1;
        const previous = segments[segments.length - 1];
        if (previous && previous.highlight === highlight && previous.degree === degree && previous.cluster === segmentCluster) {
            previous.text += text[index];
        } else {
            segments.push({text: text[index], highlight, cluster: segmentCluster, degree});
        }
    }
    return segments;
}

/**
 * Characters that form the shared base, in order.
 * @param text
 * @param degrees
 */
function commonFromDegrees(text: string, degrees: number[]): string {
    let common = '';
    for (let index = 0; index < text.length; index++) {
        if (!degrees[index]) {
            common += text[index];
        }
    }
    return common;
}

/**
 * Builds highlight plans for every visible column whose mechanism is `diff` or `eq`.
 * The comparison set is the full row list passed into the grid, including rows outside the virtualization window.
 *
 * @param rows
 * @param columns
 * @param isRowSelected
 */
export function buildCellHighlightPlans(
    rows: Array<Pick<DataGridRow, 'key' | 'cells'>> | null | undefined,
    columns: Array<Pick<DataGridColumn, 'name' | 'visible' | 'type' | 'format' | 'highlightingMechanism'>> | null | undefined,
    isRowSelected: (row: Pick<DataGridRow, 'key'>) => boolean = () => false,
): Map<string, DataGridCellHighlight> {
    const plans = new Map<string, DataGridCellHighlight>();
    const highlightColumns = (columns ?? []).filter(column =>
        !!column
        && column.visible !== false
        && (column.highlightingMechanism === DataGridColumnHighlightingMechanisms.diff
            || column.highlightingMechanism === DataGridColumnHighlightingMechanisms.eq)
        && column.name !== DataGridExoticColumnFields.selection
        && column.name !== DataGridExoticColumnFields.detailedPanel
        && column.name !== DataGridExoticColumnFields.detailedPanelToggler
        && column.name !== DataGridExoticColumnFields.spacer
    );

    for (const column of highlightColumns) {
        const mode = column.highlightingMechanism as DataGridColumnHighlightingMechanisms;
        const entries = (rows ?? []).map(row => ({
            key: highlightMapKey(row?.key, column.name),
            text: resolveCellHighlightText(column, row?.cells?.[column.name], {
                format: column.format as ((value: any) => any) | undefined,
                rowSelected: isRowSelected(row),
            }),
        }));

        const comparableEntries = entries.filter((entry): entry is { key: string, text: string } => entry.text != null);
        const texts = comparableEntries.map(entry => entry.text);
        const hasVariation = new Set(texts).size >= 2;
        const membership = hasVariation ? clusterBySimilarity(texts) : [];
        const families = new Map<number, number[]>();
        membership.forEach((cluster, index) => {
            const members = families.get(cluster) ?? [];
            members.push(index);
            families.set(cluster, members);
        });

        const colorByCluster = new Map<number, number>();
        let nextColor = 0;
        for (const [cluster, members] of families) {
            if (members.length >= 2) {
                colorByCluster.set(cluster, nextColor % HIGHLIGHT_CLUSTER_COUNT);
                nextColor++;
            }
        }

        const styleByComparableIndex = new Map<number, { degrees: number[], common: string, cluster: number }>();
        for (const [cluster, members] of families) {
            const color = colorByCluster.get(cluster);
            if (color == null) {
                continue;
            }
            const familyTexts = members.map(index => texts[index]);
            const degrees = familyDegrees(familyTexts);
            const varies = degrees.some(row => row.some(degree => degree > 0));
            if (!varies && mode === DataGridColumnHighlightingMechanisms.diff) {
                continue;
            }
            members.forEach((memberIndex, familyIndex) => {
                styleByComparableIndex.set(memberIndex, {
                    degrees: degrees[familyIndex],
                    common: commonFromDegrees(familyTexts[familyIndex], degrees[familyIndex]),
                    cluster: color,
                });
            });
        }

        let comparableIndex = 0;
        for (const entry of entries) {
            const style = entry.text == null ? undefined : styleByComparableIndex.get(comparableIndex++);
            const degrees = style?.degrees ?? null;
            const mask = degrees ? maskFromDegrees(degrees, mode) : null;
            plans.set(entry.key, {
                mode,
                text: entry.text,
                mask,
                degrees,
                cluster: style?.cluster ?? null,
                common: style?.common ?? '',
                active: !!mask?.some(Boolean),
            });
        }
    }

    return plans;
}
